import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  MainWrapper,
  MainWrapperFavorite,
} from "../../style/DasboardMain";
import Navigation from "../../components/Navigation/Navigation";
import { NotificationList } from "../../components/notification/NotificationList";
import { useNotification } from "../../components/notification/useNotification";
import ShadowTeamsList from "./components/ShadowTeamsList";
import ShadowTeamPitch from "./components/ShadowTeamPitch";
import useShadowTeamExport from "./hooks/useShadowTeamExport";
import ShadowTeamFormModal from "./components/ShadowTeamFormModal";
import ShadowTeamPlayerModal from "./components/ShadowTeamPlayerModal";
import {
  addShadowTeamPlayer,
  createShadowTeam,
  deleteShadowTeam,
  fetchShadowTeamById,
  fetchShadowTeamFormations,
  fetchShadowTeams,
  fetchShadowTeamPlayerStats,
  removeShadowTeamPlayer,
  searchShadowTeamPlayers,
  updateShadowTeam,
  updateShadowTeamPlayerPlacement,
} from "../../store/shadowTeam/shadowTeamService";
import { shadowTeamActions } from "../../store/shadowTeam/shadowTeamSlice";
import {
  buildShadowTeamSlots,
  getShadowTeamAbsolutePositionOrder,
  MAX_SHADOW_TEAM_PLAYERS_PER_SLOT,
  normalizeFormationLines,
  resolveShadowTeamAssignmentsBySlot,
  resolveShadowTeamSlotForAssignment,
} from "./utils/formation";

const StickyHeader = styled.div`
  position: sticky;
  top: 0;
  z-index: 50;
  background: ${({ theme }) => theme.workspaceBg};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 20px 0 20px;
  margin-bottom: 32px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
`;

const StickyHeaderTitle = styled.h1`
  color: ${({ theme }) => theme.white};
  font-family: "SpaceGrotesk";
  font-size: 36px;
  font-weight: 500;
  line-height: 1.2;
  margin: 0;
`;

const HowItWorksButton = styled.button`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 34px;
  background: ${({ theme }) => theme.cardBgAlt};
  border: 1px solid ${({ theme }) => theme.borderColor};
  color: ${({ theme }) => theme.green};
  padding: 8px 18px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  cursor: pointer;
  backdrop-filter: blur(2px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;

  &:hover {
    border-color: ${({ theme }) => theme.green};
    background: ${({ theme }) => theme.green};
    color: ${({ theme }) => theme.textOnAccent};
  }
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(340px, 380px);
  gap: 20px;
  padding-bottom: 40px;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`;

const CenterPanel = styled.section`
  min-height: 420px;
`;

const PlaceholderCard = styled.div`
  border: 1px solid ${({ theme }) => theme.borderColor};
  background: ${({ theme }) => theme.linesOpacity};
  border-radius: 10px;
  min-height: 420px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 24px;

  h3 {
    color: ${({ theme }) => theme.white};
    font-family: "SpaceGrotesk";
    font-size: 24px;
    margin-bottom: 10px;
  }

  p {
    color: ${({ theme }) => theme.blueish2};
    font-size: 14px;
    max-width: 520px;
  }
`;

const LoadingState = styled.p`
  color: ${({ theme }) => theme.blueish2};
  font-size: 14px;
  text-align: center;
  margin-top: 40px;
`;

const toFiniteNumber = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const SHADOW_TEAM_TUTORIAL_STORAGE_KEY = "shadow_team_tutorial_seen_v1";
const _isLightTheme = localStorage.getItem("theme") === "light";
const SHADOW_TEAM_TUTORIAL_ACCENT = _isLightTheme ? "#2363F7" : "#BEFF00";
const SHADOW_TEAM_TUTORIAL_CARD_BG = _isLightTheme ? "#ffffff" : "linear-gradient(150deg, #1e2e14, #111a0b)";
const SHADOW_TEAM_TUTORIAL_TITLE_COLOR = _isLightTheme ? "#0f1117" : "#ffffff";
const SHADOW_TEAM_TUTORIAL_BODY_COLOR = _isLightTheme ? "#6B7280" : "#8a9e80";
const SHADOW_TEAM_TUTORIAL_SKIP_COLOR = _isLightTheme ? "#9CA3AF" : "#5a6e50";
const SHADOW_TEAM_TUTORIAL_BTN_TEXT = _isLightTheme ? "#ffffff" : "#0a0e05";
const SHADOW_TEAM_TUTORIAL_STEP_INACTIVE = _isLightTheme ? "#E5E7EB" : "rgba(255,255,255,0.12)";
const SHADOW_TEAM_TUTORIAL_FONT_BASE = '"Satoshi Medium", "Satoshi-Regular", sans-serif';
const SHADOW_TEAM_TUTORIAL_FONT_MEDIUM = '"Satoshi Medium", "Satoshi-Regular", sans-serif';
const SHADOW_TEAM_TUTORIAL_FONT_BOLD = '"Satoshi Medium", "Satoshi-Regular", sans-serif';
const SHADOW_TEAM_TUTORIAL_OVERLAY_Z_INDEX = 100050;
const SHADOW_TEAM_TUTORIAL_CARD_Z_INDEX = 100060;

const buildShadowTeamTutorialSteps = (t, hasSelectedTeam) => {
  if (!hasSelectedTeam) {
    return [
      {
        id: "welcome",
        spotlight: null,
        title: t("shadowTeam.tutorialWelcomeTitle"),
        body: t("shadowTeam.tutorialWelcomeBody"),
        position: "center",
      },
      {
        id: "teams",
        spotlight: "savedTeams",
        title: t("shadowTeam.tutorialSavedTeamsTitle"),
        body: t("shadowTeam.tutorialSavedTeamsBody"),
        position: "below",
      },
      {
        id: "create-team",
        spotlight: "createTeam",
        title: t("shadowTeam.tutorialCreateTeamTitle"),
        body: t("shadowTeam.tutorialCreateTeamBody"),
        position: "below",
      },
      {
        id: "empty-workspace",
        spotlight: "emptyWorkspace",
        title: t("shadowTeam.tutorialEmptyWorkspaceTitle"),
        body: t("shadowTeam.tutorialEmptyWorkspaceBody"),
        position: "above",
      },
    ];
  }

  return [
    {
      id: "welcome",
      spotlight: null,
      title: t("shadowTeam.tutorialWelcomeTitle"),
      body: t("shadowTeam.tutorialWelcomeBody"),
      position: "center",
    },
    {
      id: "teams",
      spotlight: "savedTeams",
      title: t("shadowTeam.tutorialSavedTeamsTitle"),
      body: t("shadowTeam.tutorialSavedTeamsBody"),
      position: "below",
    },
    {
      id: "pitch",
      spotlight: "pitch",
      title: t("shadowTeam.tutorialPitchTitle"),
      body: t("shadowTeam.tutorialPitchBody"),
      position: "below",
    },
    {
      id: "roster",
      spotlight: "roster",
      title: t("shadowTeam.tutorialRosterTitle"),
      body: t("shadowTeam.tutorialRosterBody"),
      position: "above",
    },
    {
      id: "modal-search",
      spotlight: "modalSearch",
      title: t("shadowTeam.tutorialModalSearchTitle"),
      body: t("shadowTeam.tutorialModalSearchBody"),
      position: "below",
    },
    {
      id: "modal-assigned",
      spotlight: "modalAssigned",
      title: t("shadowTeam.tutorialModalAssignedTitle"),
      body: t("shadowTeam.tutorialModalAssignedBody"),
      position: "above",
    },
  ];
};

const buildRectSnapshot = (rect) => ({
  left: rect.left,
  top: rect.top,
  width: rect.width,
  height: rect.height,
  right: rect.right,
  bottom: rect.bottom,
});

const mergeElementRects = (elements) => {
  const rects = elements
    .filter(Boolean)
    .map((element) => element.getBoundingClientRect())
    .filter((rect) => rect.width > 0 && rect.height > 0);

  if (!rects.length) {
    return null;
  }

  const left = Math.min(...rects.map((rect) => rect.left));
  const top = Math.min(...rects.map((rect) => rect.top));
  const right = Math.max(...rects.map((rect) => rect.right));
  const bottom = Math.max(...rects.map((rect) => rect.bottom));

  return buildRectSnapshot({
    left,
    top,
    width: right - left,
    height: bottom - top,
    right,
    bottom,
  });
};

const ShadowTeamPage = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { notifications, addNotification, removeNotification } = useNotification();

  const {
    formations,
    teams,
    selectedTeamId,
    selectedTeam,
    loading,
    errors,
    playerStatsCache,
    playerSearch,
  } = useSelector((state) => state.shadowTeam);

  const [navigationOpen, setNavigationOpen] = useState(localStorage.getItem("navigationOpen"));
  const [formState, setFormState] = useState({
    isOpen: false,
    mode: "create",
    team: null,
  });
  const [playerModalState, setPlayerModalState] = useState({
    isOpen: false,
    slot: null,
  });
  const [pendingPlayerId, setPendingPlayerId] = useState(null);
  const [quickAddSlot, setQuickAddSlot] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [hasStartedInitialLoad, setHasStartedInitialLoad] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [tutorialStep, setTutorialStep] = useState(0);
  const [tutorialRect, setTutorialRect] = useState(null);
  const [hasResolvedTutorialVisibility, setHasResolvedTutorialVisibility] = useState(false);

  const tutorialLaunchResolvedRef = useRef(false);
  const tutorialRestoreRef = useRef({
    quickAddSlot: null,
    playerModalState: {
      isOpen: false,
      slot: null,
    },
  });
  const teamsPanelRef = useRef(null);
  const newTeamButtonRef = useRef(null);
  const pitchAreaRef = useRef(null);
  const exportButtonRef = useRef(null);
  const rosterSectionRef = useRef(null);
  const emptyWorkspaceRef = useRef(null);
  const modalSearchSectionRef = useRef(null);
  const modalAssignedSectionRef = useRef(null);

  useEffect(() => {
    dispatch(fetchShadowTeamFormations());
    dispatch(fetchShadowTeams());
    setHasStartedInitialLoad(true);
  }, [dispatch]);

  useEffect(() => {
    if (!selectedTeamId) {
      return;
    }

    dispatch(fetchShadowTeamById(selectedTeamId));
  }, [dispatch, selectedTeamId]);

  // Pre-fetch stats for every player in the selected team so club names
  // are available in the roster cards without requiring a manual hover.
  useEffect(() => {
    if (!selectedTeam?.assignments?.length) return;

    selectedTeam.assignments.forEach(({ player }) => {
      const playerId = toFiniteNumber(player?.id);
      if (playerId && !playerStatsCache[playerId]) {
        dispatch(fetchShadowTeamPlayerStats(playerId));
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTeam?.id]);

  useEffect(() => {
    const handleNavigationOpenChange = () => {
      setNavigationOpen(localStorage.getItem("navigationOpen"));
    };

    window.addEventListener("navigationOpenChange", handleNavigationOpenChange);

    return () => {
      window.removeEventListener("navigationOpenChange", handleNavigationOpenChange);
    };
  }, []);

  useEffect(() => {
    if (errors.teams) {
      addNotification(t("shadowTeam.loadError"), "error", {
        id: "shadow-load-teams",
        duration: 5000,
      });
    }
  }, [addNotification, errors.teams, t]);

  useEffect(() => {
    if (errors.formations) {
      addNotification(t("shadowTeam.loadError"), "error", {
        id: "shadow-load-formations",
        duration: 5000,
      });
    }
  }, [addNotification, errors.formations, t]);

  const selectedFormation = useMemo(() => {
    if (selectedTeam?.formation) {
      return selectedTeam.formation;
    }

    if (selectedTeam?.formationId) {
      return formations.find((formation) => formation.id === selectedTeam.formationId) || null;
    }

    return null;
  }, [formations, selectedTeam]);

  const formationLines = useMemo(
    () => normalizeFormationLines(selectedFormation?.lines),
    [selectedFormation?.lines]
  );

  const formationSlots = useMemo(() => buildShadowTeamSlots(formationLines), [formationLines]);

  const slotPlayersMap = useMemo(
    () => resolveShadowTeamAssignmentsBySlot(selectedTeam?.assignments || [], formationLines),
    [formationLines, selectedTeam?.assignments]
  );

  const selectedTeamCompletion = useMemo(() => {
    if (!selectedTeamId || !selectedTeam || selectedTeam.id !== selectedTeamId) {
      return null;
    }

    if (!formationSlots.length) {
      return null;
    }

    return {
      assigned: Object.values(slotPlayersMap).filter((players) => players.length > 0).length,
      total: formationSlots.length,
    };
  }, [formationSlots, selectedTeam, selectedTeamId, slotPlayersMap]);

  const teamCards = useMemo(() => {
    const formationNamesById = formations.reduce((accumulator, formation) => {
      if (formation?.id) {
        accumulator[formation.id] = formation.name;
      }

      return accumulator;
    }, {});

    return teams.map((team) => {
      const isActive = team.id === selectedTeamId;

      return {
        id: team.id,
        name: team.name,
        formationId: team.formationId,
        formationLabel:
          team.formation?.name ||
          team.formationName ||
          formationNamesById[team.formationId] ||
          t("shadowTeam.formation"),
        isActive,
        completion: isActive ? selectedTeamCompletion : null,
      };
    });
  }, [formations, selectedTeamCompletion, selectedTeamId, t, teams]);

  const assignedPlayerSlotKeyById = useMemo(() => {
    return (selectedTeam?.assignments || []).reduce((accumulator, assignment) => {
      const playerId = toFiniteNumber(assignment?.player?.id);
      const slot = resolveShadowTeamSlotForAssignment(formationLines, assignment);

      if (playerId === null || !slot) {
        return accumulator;
      }

      accumulator[playerId] = slot.key;
      return accumulator;
    }, {});
  }, [formationLines, selectedTeam?.assignments]);

  const activeSlotPlayers = useMemo(() => {
    if (!playerModalState.slot) {
      return [];
    }

    return slotPlayersMap[playerModalState.slot.key] || [];
  }, [playerModalState.slot, slotPlayersMap]);

  const tutorialDemoSlot = useMemo(() => {
    if (!formationSlots.length) {
      return null;
    }

    return (
      formationSlots.find((slot) => (slotPlayersMap[slot.key] || []).length > 0) ||
      formationSlots[0]
    );
  }, [formationSlots, slotPlayersMap]);

  const shadowTeamTutorialSteps = useMemo(
    () => buildShadowTeamTutorialSteps(t, Boolean(selectedTeam)),
    [selectedTeam, t]
  );

  const handlePlayerHover = useCallback(
    (playerId) => {
      const cachedStats = playerStatsCache[playerId];
      if (!playerId || cachedStats?.loading || cachedStats?.data) {
        return;
      }
      dispatch(fetchShadowTeamPlayerStats(playerId));
    },
    [dispatch, playerStatsCache]
  );

  const handleQuickAddClose = useCallback(() => {
    setQuickAddSlot(null);
    dispatch(shadowTeamActions.clearShadowTeamPlayerSearch());
  }, [dispatch]);

  const handleEmptySlotClick = useCallback(
    (slot) => {
      setQuickAddSlot(slot);
      dispatch(shadowTeamActions.clearShadowTeamPlayerSearch());
    },
    [dispatch]
  );

  const handleQuickAddSearch = useCallback(
    (query) => {
      dispatch(searchShadowTeamPlayers(query));
    },
    [dispatch]
  );

  const handleQuickAddSelect = useCallback(
    async (rawPlayer) => {
      if (!selectedTeam?.id || !quickAddSlot) return;

      const playerId = toFiniteNumber(rawPlayer?.id);
      if (playerId === null) return;

      const targetSlotPlayers = slotPlayersMap[quickAddSlot.key] || [];
      if (targetSlotPlayers.length >= MAX_SHADOW_TEAM_PLAYERS_PER_SLOT) {
        addNotification(t("shadowTeam.positionLimitError"), "warning", { duration: 3500 });
        return;
      }

      const targetPositionOrder = getShadowTeamAbsolutePositionOrder(
        quickAddSlot.positionOrder,
        targetSlotPlayers.length,
        quickAddSlot.positionGroup
      );

      try {
        await dispatch(
          addShadowTeamPlayer({
            shadowTeamId: selectedTeam.id,
            playerId,
            positionGroup: quickAddSlot.positionGroup,
            positionOrder: targetPositionOrder,
          })
        ).unwrap();

        dispatch(
          shadowTeamActions.upsertSelectedTeamAssignment({
            shadowTeamId: selectedTeam.id,
            player: rawPlayer,
            positionGroup: quickAddSlot.positionGroup,
            positionOrder: targetPositionOrder,
          })
        );

        const quickAddedTeamId = selectedTeam.id;
        handleQuickAddClose();
        addNotification(t("shadowTeam.playerAdded"), "success", {
          duration: 5000,
          actionLabel: t("shadowTeam.undoAction"),
          onAction: async () => {
            try {
              await dispatch(
                removeShadowTeamPlayer({ shadowTeamId: quickAddedTeamId, playerId })
              ).unwrap();
              dispatch(
                shadowTeamActions.removeSelectedTeamAssignment({
                  shadowTeamId: quickAddedTeamId,
                  playerId,
                })
              );
            } catch { /* silent — undo best-effort */ }
          },
        });
      } catch {
        addNotification(t("shadowTeam.playerAddError"), "error", { duration: 5000 });
      }
    },
    [dispatch, selectedTeam, quickAddSlot, slotPlayersMap, addNotification, t, handleQuickAddClose]
  );

  const handleMovePlayerOnPitch = useCallback(
    async (player, targetSlot) => {
      if (!selectedTeam?.id || !player?.id) return;

      const playerId = toFiniteNumber(player.id);
      if (playerId === null) return;

      const targetSlotPlayers = slotPlayersMap[targetSlot.key] || [];
      if (targetSlotPlayers.length >= MAX_SHADOW_TEAM_PLAYERS_PER_SLOT) {
        addNotification(t("shadowTeam.positionLimitError"), "warning", { duration: 3500 });
        return;
      }

      const targetPositionOrder = getShadowTeamAbsolutePositionOrder(
        targetSlot.positionOrder,
        targetSlotPlayers.length,
        targetSlot.positionGroup
      );

      try {
        await dispatch(
          updateShadowTeamPlayerPlacement({
            shadowTeamId: selectedTeam.id,
            playerId,
            positionGroup: targetSlot.positionGroup,
            positionOrder: targetPositionOrder,
          })
        ).unwrap();

        dispatch(
          shadowTeamActions.upsertSelectedTeamAssignment({
            shadowTeamId: selectedTeam.id,
            player,
            positionGroup: targetSlot.positionGroup,
            positionOrder: targetPositionOrder,
          })
        );

        addNotification(t("shadowTeam.playerMoved"), "success", { duration: 2500 });
      } catch {
        addNotification(t("shadowTeam.playerMoveError"), "error", { duration: 5000 });
      }
    },
    [dispatch, selectedTeam, slotPlayersMap, addNotification, t]
  );

  const { exportToPdf } = useShadowTeamExport({
    shadowTeamId: selectedTeam?.id,
    teamName: selectedTeam?.name,
  });

  const handleExportToPdf = useCallback(async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      await exportToPdf();
    } catch {
      addNotification(t("shadowTeam.exportError"), "error", { duration: 5000 });
    } finally {
      setIsExporting(false);
    }
  }, [exportToPdf, isExporting, addNotification, t]);

  const startShadowTeamTutorial = useCallback(() => {
    tutorialRestoreRef.current = {
      quickAddSlot,
      playerModalState: {
        isOpen: playerModalState.isOpen,
        slot: playerModalState.slot || null,
      },
    };

    setTutorialStep(0);
    setTutorialRect(null);
    setShowTutorial(true);
  }, [playerModalState.isOpen, playerModalState.slot, quickAddSlot]);

  const completeShadowTeamTutorial = useCallback(() => {
    const {
      quickAddSlot: restoredQuickAddSlot,
      playerModalState: restoredPlayerModalState,
    } = tutorialRestoreRef.current;

    try {
      localStorage.setItem(SHADOW_TEAM_TUTORIAL_STORAGE_KEY, "1");
    } catch {
      // noop
    }

    tutorialRestoreRef.current = {
      quickAddSlot: null,
      playerModalState: {
        isOpen: false,
        slot: null,
      },
    };

    setShowTutorial(false);
    setTutorialRect(null);
    setQuickAddSlot(restoredQuickAddSlot || null);
    setPlayerModalState(
      restoredPlayerModalState?.isOpen
        ? {
            isOpen: true,
            slot: restoredPlayerModalState.slot || null,
          }
        : {
            isOpen: false,
            slot: null,
          }
    );
    dispatch(shadowTeamActions.clearShadowTeamPlayerSearch());
  }, [dispatch]);

  const handleTutorialNext = useCallback(() => {
    if (tutorialStep >= shadowTeamTutorialSteps.length - 1) {
      completeShadowTeamTutorial();
      return;
    }

    setTutorialStep((prev) => prev + 1);
  }, [completeShadowTeamTutorial, shadowTeamTutorialSteps.length, tutorialStep]);

  const handleTutorialSkip = useCallback(() => {
    completeShadowTeamTutorial();
  }, [completeShadowTeamTutorial]);

  const handleTutorialReplay = useCallback(() => {
    startShadowTeamTutorial();
  }, [startShadowTeamTutorial]);

  useEffect(() => {
    if (tutorialLaunchResolvedRef.current) {
      return;
    }

    if (!hasStartedInitialLoad || loading.formations || loading.teams) {
      return;
    }

    if (teams.length > 0 && !selectedTeam && !loading.selectedTeam && !errors.selectedTeam) {
      return;
    }

    tutorialLaunchResolvedRef.current = true;
    setHasResolvedTutorialVisibility(true);

    try {
      if (localStorage.getItem(SHADOW_TEAM_TUTORIAL_STORAGE_KEY) !== "1") {
        startShadowTeamTutorial();
      }
    } catch {
      startShadowTeamTutorial();
    }
  }, [
    errors.selectedTeam,
    hasStartedInitialLoad,
    loading.formations,
    loading.teams,
    loading.selectedTeam,
    selectedTeam?.id,
    startShadowTeamTutorial,
    teams.length,
  ]);

  const tutorialCurrentStep = showTutorial ? shadowTeamTutorialSteps[tutorialStep] : null;
  const tutorialUsesModal =
    tutorialCurrentStep?.spotlight === "modalSearch" ||
    tutorialCurrentStep?.spotlight === "modalAssigned";

  useEffect(() => {
    if (!showTutorial) {
      return;
    }

    if (quickAddSlot) {
      handleQuickAddClose();
    }

    dispatch(shadowTeamActions.clearShadowTeamPlayerSearch());

    if (!tutorialUsesModal) {
      if (playerModalState.isOpen) {
        setPlayerModalState({
          isOpen: false,
          slot: null,
        });
      }

      return;
    }

    if (!tutorialDemoSlot) {
      return;
    }

    if (
      !playerModalState.isOpen ||
      playerModalState.slot?.key !== tutorialDemoSlot.key
    ) {
      setPlayerModalState({
        isOpen: true,
        slot: tutorialDemoSlot,
      });
    }
  }, [
    dispatch,
    handleQuickAddClose,
    playerModalState.isOpen,
    playerModalState.slot,
    quickAddSlot,
    showTutorial,
    tutorialDemoSlot,
    tutorialUsesModal,
  ]);

  useEffect(() => {
    if (!showTutorial) {
      return undefined;
    }

    if (!tutorialCurrentStep?.spotlight) {
      setTutorialRect(null);
      return undefined;
    }

    const updateRect = () => {
      let nextRect = null;

      switch (tutorialCurrentStep.spotlight) {
        case "savedTeams":
          nextRect = mergeElementRects([teamsPanelRef.current]);
          break;
        case "createTeam":
          nextRect = mergeElementRects([newTeamButtonRef.current]);
          break;
        case "pitch":
          nextRect = mergeElementRects([pitchAreaRef.current, exportButtonRef.current]);
          break;
        case "roster":
          nextRect = mergeElementRects([rosterSectionRef.current]);
          break;
        case "modalSearch":
          nextRect = mergeElementRects([modalSearchSectionRef.current]);
          break;
        case "modalAssigned":
          nextRect = mergeElementRects([modalAssignedSectionRef.current]);
          break;
        case "emptyWorkspace":
          nextRect = mergeElementRects([emptyWorkspaceRef.current]);
          break;
        default:
          nextRect = null;
      }

      setTutorialRect(nextRect);
    };

    // Scroll the roster section into view before measuring, so the rect
    // reflects its actual on-screen position after the page has scrolled.
    if (tutorialCurrentStep.spotlight === "roster" && rosterSectionRef.current) {
      rosterSectionRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }

    const scrollDelay = tutorialCurrentStep.spotlight === "roster" ? 420 : 70;
    const timeoutId = window.setTimeout(updateRect, scrollDelay);
    const animationFrameId =
      typeof window.requestAnimationFrame === "function"
        ? window.requestAnimationFrame(updateRect)
        : null;

    window.addEventListener("resize", updateRect);
    window.addEventListener("scroll", updateRect, true);

    return () => {
      window.clearTimeout(timeoutId);
      if (animationFrameId !== null && typeof window.cancelAnimationFrame === "function") {
        window.cancelAnimationFrame(animationFrameId);
      }
      window.removeEventListener("resize", updateRect);
      window.removeEventListener("scroll", updateRect, true);
    };
  }, [
    activeSlotPlayers.length,
    playerModalState.isOpen,
    playerModalState.slot,
    selectedTeam?.id,
    showTutorial,
    teamCards.length,
    tutorialCurrentStep,
  ]);

  const tutorialIsLastStep = tutorialStep >= shadowTeamTutorialSteps.length - 1;
  const tutorialSpotlightPadding = 10;
  const tutorialSpotlightStyle = tutorialRect
    ? {
        left: tutorialRect.left - tutorialSpotlightPadding,
        top: tutorialRect.top - tutorialSpotlightPadding,
        width: tutorialRect.width + tutorialSpotlightPadding * 2,
        height: tutorialRect.height + tutorialSpotlightPadding * 2,
      }
    : null;

  const { tutorialCardStyle, tutorialCardArrowSide } = (() => {
    const CARD_W = 360;
    const CARD_H = 240; // conservative estimated card height
    const MARGIN = 16;
    const GAP = tutorialSpotlightPadding + 16;
    const vw = typeof window !== "undefined" ? window.innerWidth : CARD_W + 40;
    const vh = typeof window !== "undefined" ? window.innerHeight : 700;

    const mobileStyle = {
      position: "fixed",
      left: 12,
      right: 12,
      bottom: 12,
      top: "auto",
      transform: "none",
      width: "auto",
    };

    if (!tutorialCurrentStep) {
      return {
        tutorialCardStyle: { position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: CARD_W },
        tutorialCardArrowSide: null,
      };
    }

    if (vw <= 480) {
      return { tutorialCardStyle: mobileStyle, tutorialCardArrowSide: null };
    }

    // Welcome / center / no rect: bottom-right corner
    if (tutorialCurrentStep.id === "welcome" || !tutorialRect || tutorialCurrentStep.position === "center") {
      return {
        tutorialCardStyle: { position: "fixed", right: 24, bottom: 24, width: CARD_W, left: "auto", top: "auto", transform: "none" },
        tutorialCardArrowSide: null,
      };
    }

    // Horizontal: center on spotlight, clamped to viewport edges
    let left = tutorialRect.left + tutorialRect.width / 2 - CARD_W / 2;
    if (tutorialCurrentStep.spotlight === "savedTeams") {
      left -= 30;
    }
    left = Math.max(MARGIN, Math.min(left, vw - CARD_W - MARGIN));

    let top;
    let arrowSide;
    const spaceBelow = vh - (tutorialRect.bottom + GAP) - MARGIN;
    const spaceAbove = tutorialRect.top - GAP - MARGIN;

    if (tutorialCurrentStep.position === "below") {
      if (spaceBelow >= CARD_H) {
        top = tutorialRect.bottom + GAP;
        arrowSide = "top";
      } else {
        // flip above
        top = Math.max(MARGIN, tutorialRect.top - GAP - CARD_H);
        arrowSide = "bottom";
      }
    } else {
      // "above"
      if (spaceAbove >= CARD_H) {
        top = tutorialRect.top - GAP - CARD_H;
        arrowSide = "bottom";
      } else {
        // flip below
        top = tutorialRect.bottom + GAP;
        arrowSide = "top";
      }
    }

    top = Math.max(MARGIN, Math.min(top, vh - CARD_H - MARGIN));

    return {
      tutorialCardStyle: { position: "fixed", top, left, width: CARD_W },
      tutorialCardArrowSide: arrowSide,
    };
  })();

  const handleSelectTeam = (teamId) => {
    dispatch(shadowTeamActions.setSelectedShadowTeamId(teamId));
  };

  const handleCreateClick = () => {
    setFormState({
      isOpen: true,
      mode: "create",
      team: null,
    });
  };

  const handleEditClick = (team) => {
    const selected = selectedTeam?.id === team?.id ? selectedTeam : team;

    setFormState({
      isOpen: true,
      mode: "edit",
      team: selected,
    });
  };

  const handleDeleteClick = async (team) => {
    if (!team?.id) {
      return;
    }

    const confirmed = window.confirm(t("shadowTeam.confirmDeleteBody"));
    if (!confirmed) {
      return;
    }

    try {
      await dispatch(deleteShadowTeam(team.id)).unwrap();
      addNotification(t("shadowTeam.teamDeleted"), "success", { duration: 2500 });
      await dispatch(fetchShadowTeams()).unwrap();
    } catch {
      addNotification(t("shadowTeam.deleteError"), "error", { duration: 5000 });
    }
  };

  const handleSubmitTeamForm = async ({ name, formationId }) => {
    if (formState.mode === "create") {
      try {
        const createdTeam = await dispatch(
          createShadowTeam({
            name,
            formation_id: formationId,
          })
        ).unwrap();

        const createdTeamId = toFiniteNumber(createdTeam?.id);

        addNotification(t("shadowTeam.teamCreated"), "success", { duration: 2500 });
        setFormState({ isOpen: false, mode: "create", team: null });

        await dispatch(fetchShadowTeams()).unwrap();

        if (createdTeamId !== null) {
          dispatch(shadowTeamActions.setSelectedShadowTeamId(createdTeamId));
          await dispatch(fetchShadowTeamById(createdTeamId)).unwrap();
        }
      } catch {
        addNotification(t("shadowTeam.saveError"), "error", { duration: 5000 });
      }

      return;
    }

    const editableTeam = formState.team || selectedTeam;
    const teamId = toFiniteNumber(editableTeam?.id);

    if (teamId === null) {
      addNotification(t("shadowTeam.saveError"), "error", { duration: 5000 });
      return;
    }

    try {
      await dispatch(
        updateShadowTeam({
          id: teamId,
          payload: {
            name,
            formation_id: formationId,
          },
        })
      ).unwrap();

      addNotification(t("shadowTeam.teamUpdated"), "success", { duration: 2500 });
      setFormState({ isOpen: false, mode: "create", team: null });

      await dispatch(fetchShadowTeams()).unwrap();
      // Do NOT re-fetch the team detail here — the updateShadowTeam.fulfilled reducer
      // already applies the new formation to selectedTeam while preserving all player
      // assignments. Re-fetching would overwrite assignments with whatever the server
      // returns for the new formation (typically empty).
    } catch {
      addNotification(t("shadowTeam.saveError"), "error", { duration: 5000 });
    }
  };

  const handleOpenSlotModal = (slot) => {
    if (!selectedTeam?.id) {
      return;
    }

    setPlayerModalState({
      isOpen: true,
      slot,
    });
  };

  const handleSelectPlayerForSlot = async (player) => {
    if (!selectedTeam?.id || !playerModalState.slot) {
      return;
    }

    const playerId = toFiniteNumber(player?.id);
    if (playerId === null) {
      return;
    }

    const targetSlotPlayers = slotPlayersMap[playerModalState.slot.key] || [];
    const currentSlotKey = assignedPlayerSlotKeyById[playerId] || null;

    if (currentSlotKey === playerModalState.slot.key) {
      return;
    }

    if (targetSlotPlayers.length >= MAX_SHADOW_TEAM_PLAYERS_PER_SLOT) {
      addNotification(t("shadowTeam.positionLimitError"), "warning", { duration: 3500 });
      return;
    }

    const currentAssignment = (selectedTeam?.assignments || []).find(
      (assignment) => toFiniteNumber(assignment?.player?.id) === playerId
    );
    const targetPositionOrder = getShadowTeamAbsolutePositionOrder(
      playerModalState.slot.positionOrder,
      targetSlotPlayers.length,
      playerModalState.slot.positionGroup
    );

    setPendingPlayerId(playerId);

    try {
      if (currentAssignment) {
        await dispatch(
          updateShadowTeamPlayerPlacement({
            shadowTeamId: selectedTeam.id,
            playerId,
            positionGroup: playerModalState.slot.positionGroup,
            positionOrder: targetPositionOrder,
          })
        ).unwrap();

        dispatch(
          shadowTeamActions.upsertSelectedTeamAssignment({
            shadowTeamId: selectedTeam.id,
            player,
            positionGroup: playerModalState.slot.positionGroup,
            positionOrder: targetPositionOrder,
          })
        );

        addNotification(t("shadowTeam.playerMoved"), "success", { duration: 2500 });
        return;
      }

      await dispatch(
        addShadowTeamPlayer({
          shadowTeamId: selectedTeam.id,
          playerId,
          positionGroup: playerModalState.slot.positionGroup,
          positionOrder: targetPositionOrder,
        })
      ).unwrap();

      dispatch(
        shadowTeamActions.upsertSelectedTeamAssignment({
          shadowTeamId: selectedTeam.id,
          player,
          positionGroup: playerModalState.slot.positionGroup,
          positionOrder: targetPositionOrder,
        })
      );

      const addedTeamId = selectedTeam.id;
      addNotification(t("shadowTeam.playerAdded"), "success", {
        duration: 5000,
        actionLabel: t("shadowTeam.undoAction"),
        onAction: async () => {
          try {
            await dispatch(
              removeShadowTeamPlayer({ shadowTeamId: addedTeamId, playerId })
            ).unwrap();
            dispatch(
              shadowTeamActions.removeSelectedTeamAssignment({
                shadowTeamId: addedTeamId,
                playerId,
              })
            );
          } catch { /* silent — undo best-effort */ }
        },
      });
    } catch {
      addNotification(
        currentAssignment ? t("shadowTeam.playerMoveError") : t("shadowTeam.playerAddError"),
        "error",
        { duration: 5000 }
      );
    } finally {
      setPendingPlayerId(null);
    }
  };

  const reorderPlayersInSlot = useCallback(async (slot, players, draggedPlayerId, targetPlayerId) => {
    if (!selectedTeam?.id || !slot) {
      return;
    }

    const sourceIndex = players.findIndex(
      (player) => toFiniteNumber(player?.id) === toFiniteNumber(draggedPlayerId)
    );
    const targetIndex = players.findIndex(
      (player) => toFiniteNumber(player?.id) === toFiniteNumber(targetPlayerId)
    );

    if (sourceIndex === -1 || targetIndex === -1 || sourceIndex === targetIndex) {
      return;
    }

    const nextPlayers = [...players];
    const [draggedPlayer] = nextPlayers.splice(sourceIndex, 1);
    nextPlayers.splice(targetIndex, 0, draggedPlayer);

    setPendingPlayerId(toFiniteNumber(draggedPlayerId));

    try {
      for (let index = 0; index < nextPlayers.length; index += 1) {
        const player = nextPlayers[index];
        const playerId = toFiniteNumber(player?.id);
        const nextPositionOrder = getShadowTeamAbsolutePositionOrder(
          slot.positionOrder,
          index,
          slot.positionGroup
        );

        if (playerId === null || nextPositionOrder === null) {
          // eslint-disable-next-line no-continue
          continue;
        }

        // eslint-disable-next-line no-await-in-loop
        await dispatch(
          updateShadowTeamPlayerPlacement({
            shadowTeamId: selectedTeam.id,
            playerId,
            positionGroup: slot.positionGroup,
            positionOrder: nextPositionOrder,
          })
        ).unwrap();

        dispatch(
          shadowTeamActions.upsertSelectedTeamAssignment({
            shadowTeamId: selectedTeam.id,
            player,
            positionGroup: slot.positionGroup,
            positionOrder: nextPositionOrder,
          })
        );
      }

      addNotification(t("shadowTeam.playerPriorityUpdated"), "success", { duration: 2500 });
    } catch {
      addNotification(t("shadowTeam.playerPriorityError"), "error", { duration: 5000 });
      await dispatch(fetchShadowTeamById(selectedTeam.id));
    } finally {
      setPendingPlayerId(null);
    }
  }, [addNotification, dispatch, selectedTeam?.id, t]);

  const handleReorderPlayerPriority = useCallback(
    (draggedPlayerId, targetPlayerId) => {
      if (!playerModalState.slot) {
        return Promise.resolve();
      }

      return reorderPlayersInSlot(
        playerModalState.slot,
        activeSlotPlayers,
        draggedPlayerId,
        targetPlayerId
      );
    },
    [activeSlotPlayers, playerModalState.slot, reorderPlayersInSlot]
  );

  const handleRosterReorderPlayerPriority = useCallback(
    (slot, draggedPlayerId, targetPlayerId) => {
      const slotPlayers = slotPlayersMap[slot?.key] || [];
      return reorderPlayersInSlot(slot, slotPlayers, draggedPlayerId, targetPlayerId);
    },
    [reorderPlayersInSlot, slotPlayersMap]
  );

  const handleRemovePlayerFromSlot = async (player) => {
    if (!selectedTeam?.id) {
      return;
    }

    const playerId = toFiniteNumber(player?.id);
    if (playerId === null) {
      return;
    }

    // Capture assignment before removal so undo can restore it
    const priorAssignment = (selectedTeam?.assignments || []).find(
      (a) => toFiniteNumber(a?.player?.id) === playerId
    );

    setPendingPlayerId(playerId);

    try {
      await dispatch(
        removeShadowTeamPlayer({
          shadowTeamId: selectedTeam.id,
          playerId,
        })
      ).unwrap();

      dispatch(
        shadowTeamActions.removeSelectedTeamAssignment({
          shadowTeamId: selectedTeam.id,
          playerId,
        })
      );

      const removedTeamId = selectedTeam.id;
      const priorPositionGroup = priorAssignment?.positionGroup ?? null;
      const priorPositionOrder = priorAssignment?.positionOrder ?? null;

      addNotification(t("shadowTeam.playerRemoved"), "success", {
        duration: 5000,
        actionLabel: t("shadowTeam.undoAction"),
        onAction: async () => {
          try {
            await dispatch(
              addShadowTeamPlayer({
                shadowTeamId: removedTeamId,
                playerId,
                positionGroup: priorPositionGroup,
                positionOrder: priorPositionOrder,
              })
            ).unwrap();
            dispatch(
              shadowTeamActions.upsertSelectedTeamAssignment({
                shadowTeamId: removedTeamId,
                player,
                positionGroup: priorPositionGroup,
                positionOrder: priorPositionOrder,
              })
            );
          } catch { /* silent — undo best-effort */ }
        },
      });
    } catch {
      addNotification(t("shadowTeam.playerRemoveError"), "error", { duration: 5000 });
    } finally {
      setPendingPlayerId(null);
    }
  };

  return (
    <MainWrapper>
      <Navigation />

      <MainWrapperFavorite
        className={navigationOpen === "true" ? "" : "fullWidthPage"}
      >
        <StickyHeader>
          <StickyHeaderTitle>{t("shadowTeam.pageTitle")}</StickyHeaderTitle>
          {hasResolvedTutorialVisibility && !showTutorial ? (
            <HowItWorksButton type="button" onClick={handleTutorialReplay}>
              {t("shadowTeam.howItWorks")}
            </HowItWorksButton>
          ) : null}
        </StickyHeader>

        <ContentGrid>
          <CenterPanel>
            {loading.selectedTeam && selectedTeamId ? (
              <LoadingState>{t("shadowTeam.searching")}</LoadingState>
            ) : null}

            {!loading.selectedTeam && selectedTeam ? (
              <ShadowTeamPitch
                formationName={selectedFormation?.name}
                formationLines={formationLines}
                slotPlayersMap={slotPlayersMap}
                onAddPlayer={handleOpenSlotModal}
                playerStatsCache={playerStatsCache}
                onPlayerHover={handlePlayerHover}
                onMovePlayer={handleMovePlayerOnPitch}
                onReorderPlayers={handleRosterReorderPlayerPriority}
                onRemovePlayer={handleRemovePlayerFromSlot}
                quickAddSlot={quickAddSlot}
                quickAddResults={playerSearch.results}
                quickAddLoading={playerSearch.loading}
                onEmptySlotClick={handleEmptySlotClick}
                onQuickAddSearch={handleQuickAddSearch}
                onQuickAddSelect={handleQuickAddSelect}
                onQuickAddClose={handleQuickAddClose}
                onExport={handleExportToPdf}
                isExporting={isExporting}
                tutorialPitchRef={pitchAreaRef}
                tutorialExportRef={exportButtonRef}
                tutorialRosterRef={rosterSectionRef}
              />
            ) : null}

            {!loading.selectedTeam && !selectedTeam ? (
              <PlaceholderCard ref={emptyWorkspaceRef}>
                <h3>{t("shadowTeam.noTeamsTitle")}</h3>
                <p>{t("shadowTeam.noTeamsBody")}</p>
              </PlaceholderCard>
            ) : null}
          </CenterPanel>

          <ShadowTeamsList
            teamCards={teamCards}
            onSelect={handleSelectTeam}
            onCreate={handleCreateClick}
            onEdit={handleEditClick}
            onDelete={handleDeleteClick}
            tutorialPanelRef={teamsPanelRef}
            tutorialCreateButtonRef={newTeamButtonRef}
          />
        </ContentGrid>

        <NotificationList notifications={notifications} onClose={removeNotification} />
      </MainWrapperFavorite>

      <ShadowTeamFormModal
        isOpen={formState.isOpen}
        mode={formState.mode}
        formations={formations}
        initialName={formState.team?.name || ""}
        initialFormationId={
          formState.team?.formationId ??
          selectedTeam?.formationId ??
          formations[0]?.id ??
          null
        }
        isLoading={loading.createTeam || loading.updateTeam}
        onClose={() => setFormState({ isOpen: false, mode: "create", team: null })}
        onSubmit={handleSubmitTeamForm}
      />

      <ShadowTeamPlayerModal
        isOpen={playerModalState.isOpen}
        slot={playerModalState.slot}
        assignedPlayers={activeSlotPlayers}
        assignedPlayerSlotKeyById={assignedPlayerSlotKeyById}
        pendingPlayerId={pendingPlayerId}
        onClose={() => {
          setPlayerModalState({ isOpen: false, slot: null });
          dispatch(shadowTeamActions.clearShadowTeamPlayerSearch());
        }}
        onSelectPlayer={handleSelectPlayerForSlot}
        onRemovePlayer={handleRemovePlayerFromSlot}
        onReorderPlayers={handleReorderPlayerPriority}
        tutorialSearchSectionRef={modalSearchSectionRef}
        tutorialAssignedSectionRef={modalAssignedSectionRef}
      />


      {showTutorial && tutorialCurrentStep ? (
        <>
          {tutorialCurrentStep.spotlight && tutorialRect ? (
            <svg
              style={{
                position: "fixed",
                inset: 0,
                width: "100%",
                height: "100%",
                pointerEvents: "auto",
                zIndex: SHADOW_TEAM_TUTORIAL_OVERLAY_Z_INDEX,
              }}
              xmlns="http://www.w3.org/2000/svg"
            >
              <defs>
                <mask id="shadow-team-tutorial-spotlight-mask">
                  <rect width="100%" height="100%" fill="white" />
                  <rect
                    x={tutorialRect.left - tutorialSpotlightPadding}
                    y={tutorialRect.top - tutorialSpotlightPadding}
                    width={tutorialRect.width + tutorialSpotlightPadding * 2}
                    height={tutorialRect.height + tutorialSpotlightPadding * 2}
                    rx="12"
                    fill="black"
                  />
                </mask>
              </defs>
              <rect
                width="100%"
                height="100%"
                fill="rgba(0,0,0,0.78)"
                mask="url(#shadow-team-tutorial-spotlight-mask)"
              />
            </svg>
          ) : (
            <div
              style={{
                position: "fixed",
                inset: 0,
                background: "rgba(0,0,0,0.82)",
                zIndex: SHADOW_TEAM_TUTORIAL_OVERLAY_Z_INDEX,
                pointerEvents: "auto",
              }}
            />
          )}

          {tutorialSpotlightStyle ? (
            <div
              style={{
                position: "fixed",
                ...tutorialSpotlightStyle,
                borderRadius: 14,
                border: `2px solid ${SHADOW_TEAM_TUTORIAL_ACCENT}`,
                boxShadow: `0 0 0 1px ${SHADOW_TEAM_TUTORIAL_ACCENT}44, 0 0 24px ${SHADOW_TEAM_TUTORIAL_ACCENT}66, 0 0 48px ${SHADOW_TEAM_TUTORIAL_ACCENT}22`,
                zIndex: SHADOW_TEAM_TUTORIAL_OVERLAY_Z_INDEX + 1,
                pointerEvents: "none",
                animation: "shadowTeamTutorialPulse 2s ease-in-out infinite",
              }}
            />
          ) : null}

          <div
            key={tutorialCurrentStep.id}
            style={{
              ...tutorialCardStyle,
              zIndex: SHADOW_TEAM_TUTORIAL_CARD_Z_INDEX,
              background: SHADOW_TEAM_TUTORIAL_CARD_BG,
              border: _isLightTheme ? "1px solid #E5E7EB" : `1.5px solid ${SHADOW_TEAM_TUTORIAL_ACCENT}66`,
              borderRadius: 20,
              padding: "26px 26px 20px",
              boxShadow: _isLightTheme
                ? "0 8px 32px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)"
                : `0 32px 80px rgba(0,0,0,0.88), 0 0 0 1px ${SHADOW_TEAM_TUTORIAL_ACCENT}22, 0 0 40px ${SHADOW_TEAM_TUTORIAL_ACCENT}18, inset 0 1px 0 ${SHADOW_TEAM_TUTORIAL_ACCENT}22`,
              fontFamily: SHADOW_TEAM_TUTORIAL_FONT_BASE,
            }}
          >

            {/* Arrow pointing toward spotlight */}
            {tutorialCardArrowSide === "top" ? (
              <div style={{
                position: "absolute",
                top: -9,
                left: "50%",
                transform: "translateX(-50%)",
                width: 0,
                height: 0,
                borderLeft: "9px solid transparent",
                borderRight: "9px solid transparent",
                borderBottom: `9px solid ${SHADOW_TEAM_TUTORIAL_ACCENT}88`,
                filter: `drop-shadow(0 -2px 4px ${SHADOW_TEAM_TUTORIAL_ACCENT}44)`,
              }} />
            ) : tutorialCardArrowSide === "bottom" ? (
              <div style={{
                position: "absolute",
                bottom: -9,
                left: "50%",
                transform: "translateX(-50%)",
                width: 0,
                height: 0,
                borderLeft: "9px solid transparent",
                borderRight: "9px solid transparent",
                borderTop: `9px solid ${SHADOW_TEAM_TUTORIAL_ACCENT}88`,
                filter: `drop-shadow(0 2px 4px ${SHADOW_TEAM_TUTORIAL_ACCENT}44)`,
              }} />
            ) : null}

            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
              {shadowTeamTutorialSteps.map((stepItem, index) => (
                <div
                  key={stepItem.id}
                  style={{
                    height: 3,
                    flex: 1,
                    borderRadius: 99,
                    background:
                      index <= tutorialStep
                        ? SHADOW_TEAM_TUTORIAL_ACCENT
                        : SHADOW_TEAM_TUTORIAL_STEP_INACTIVE,
                    transition: "background 0.4s",
                    boxShadow: index <= tutorialStep ? `0 0 6px ${SHADOW_TEAM_TUTORIAL_ACCENT}88` : "none",
                  }}
                />
              ))}
              <span style={{
                fontSize: 10,
                color: _isLightTheme ? SHADOW_TEAM_TUTORIAL_ACCENT : `${SHADOW_TEAM_TUTORIAL_ACCENT}99`,
                fontFamily: SHADOW_TEAM_TUTORIAL_FONT_MEDIUM,
                fontWeight: 700,
                letterSpacing: "0.05em",
                flexShrink: 0,
                marginLeft: 2,
              }}>
                {tutorialStep + 1}/{shadowTeamTutorialSteps.length}
              </span>
            </div>

            {tutorialCurrentStep.id === "welcome" ? (
              <div
                style={{
                  width: 52,
                  height: 52,
                  borderRadius: 14,
                  background: `linear-gradient(135deg, ${SHADOW_TEAM_TUTORIAL_ACCENT}33, ${SHADOW_TEAM_TUTORIAL_ACCENT}11)`,
                  border: `1px solid ${SHADOW_TEAM_TUTORIAL_ACCENT}55`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 24,
                  marginBottom: 16,
                }}
              >
                <svg
                  width="26"
                  height="26"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{
                    filter:
                      "drop-shadow(0 0 4px rgba(255, 218, 0, 0.55)) drop-shadow(0 0 8px rgba(174, 119, 20, 0.35))",
                  }}
                  aria-hidden="true"
                >
                  <defs>
                    <linearGradient
                      id="shadowTeamTutorialBoltGradient"
                      x1="15.5"
                      y1="1.5"
                      x2="6.5"
                      y2="22.5"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop offset="0%" stopColor="#FFDA00" />
                      <stop offset="100%" stopColor="#AE7714" />
                    </linearGradient>
                  </defs>
                  <path
                    d="M13.8 1.5L5.5 11.8h5.5L9.9 22.5l8.6-12h-5L13.8 1.5z"
                    fill="url(#shadowTeamTutorialBoltGradient)"
                    stroke="#FFF4B3"
                    strokeWidth="0.4"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            ) : null}

            <h2
              style={{
                margin: "0 0 10px",
                fontSize: tutorialCurrentStep.id === "welcome" ? 22 : 18,
                fontWeight: 800,
                fontFamily: SHADOW_TEAM_TUTORIAL_FONT_BOLD,
                color: SHADOW_TEAM_TUTORIAL_TITLE_COLOR,
                lineHeight: 1.25,
                whiteSpace: "pre-line",
                letterSpacing: -0.3,
              }}
            >
              {tutorialCurrentStep.title}
            </h2>

            <p
              style={{
                margin: "0 0 24px",
                fontSize: 13.5,
                fontFamily: SHADOW_TEAM_TUTORIAL_FONT_BASE,
                color: SHADOW_TEAM_TUTORIAL_BODY_COLOR,
                lineHeight: 1.6,
              }}
            >
              {tutorialCurrentStep.body}
            </p>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 10,
              }}
            >
              <button
                type="button"
                onClick={handleTutorialSkip}
                style={{
                  background: "transparent",
                  border: "none",
                  color: SHADOW_TEAM_TUTORIAL_SKIP_COLOR,
                  fontSize: 13,
                  cursor: "pointer",
                  padding: "6px 0",
                  fontFamily: SHADOW_TEAM_TUTORIAL_FONT_MEDIUM,
                  fontWeight: 500,
                  textDecoration: "underline",
                  textUnderlineOffset: 3,
                }}
              >
                {t("shadowTeam.tutorialSkip")}
              </button>

              <button
                type="button"
                onClick={handleTutorialNext}
                style={{
                  background: SHADOW_TEAM_TUTORIAL_ACCENT,
                  border: "none",
                  color: SHADOW_TEAM_TUTORIAL_BTN_TEXT,
                  fontFamily: SHADOW_TEAM_TUTORIAL_FONT_BOLD,
                  fontWeight: 800,
                  fontSize: 13.5,
                  padding: "10px 24px",
                  borderRadius: 10,
                  cursor: "pointer",
                  letterSpacing: 0.3,
                  boxShadow: `0 4px 20px ${SHADOW_TEAM_TUTORIAL_ACCENT}55`,
                }}
              >
                {tutorialIsLastStep
                  ? t("shadowTeam.tutorialDone")
                  : t("shadowTeam.tutorialNext")}
              </button>
            </div>
          </div>

          <style>
            {`
              @keyframes shadowTeamTutorialPulse {
                0%,100% { box-shadow: 0 0 0 1px ${SHADOW_TEAM_TUTORIAL_ACCENT}44, 0 0 20px ${SHADOW_TEAM_TUTORIAL_ACCENT}55, 0 0 40px ${SHADOW_TEAM_TUTORIAL_ACCENT}22; }
                50% { box-shadow: 0 0 0 2px ${SHADOW_TEAM_TUTORIAL_ACCENT}88, 0 0 32px ${SHADOW_TEAM_TUTORIAL_ACCENT}99, 0 0 64px ${SHADOW_TEAM_TUTORIAL_ACCENT}44; }
              }
            `}
          </style>
        </>
      ) : null}
    </MainWrapper>
  );
};

export default ShadowTeamPage;
